import request from "./SuperagentUtils";
import verbiage from "../components/companyProfile/file-validation-verbiage";
import $ from "jquery";

const fileNameIsValid = (fileName: string): boolean => {
  if (!fileName) return false;
  const name = fileName.trim();
  return name.length > 0 && name.length < 256;
};

export const sanitizeFileName = (fileName: string): string => {
  const fileNameSplit = fileName.toLowerCase().split('.');

  if (fileNameSplit.length < 2) {
    // Remove leading and trailing whitespace, and replace any non-alphanumeric characters with a hyphen
    return fileName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '-');
  }
  const name = fileNameSplit[0].trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '-');
  const extension = fileNameSplit[fileNameSplit.length-1].trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '-');
  return `${name}.${extension}`;
}

const isSupportedFileType = (file: File): boolean => {
  const fileType = file.type;
  const ValidFileTypes = ['image/gif', 'image/jpeg', 'image/png', 'application/pdf'];
  return $.inArray(fileType, ValidFileTypes) >= 0;
}

/**
 * Creates a timestamped filename by prepending the current timestamp to the original filename.
 * Format: `${timestamp}_${fileName}`
 *
 * @param fileName The original filename
 * @returns The timestamped filename (e.g., "1629302400000_photo.jpg")
 */
export const createTimestampedFileName = (fileName: string): string => {
  return `${new Date().getTime()}_${fileName}`;
};

interface UploadFile {
  files?: FileList;
  isImageFile?: boolean;
}

export const validateFiles = (allUploadFiles: UploadFile[]): boolean => {
  for (let i = 0; i < allUploadFiles.length; i++) {
    const fileToUpload = allUploadFiles[i];
    const file = fileToUpload.files ? fileToUpload.files[0] : null;
    if (!file) continue;

    if (file.size > 20000000) {
      return false;
    }
    if (fileToUpload.isImageFile) {
      if (file && !isSupportedFileType(file)) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Validates the file and return an error message if the file is invalid or empty if the file is valid.
 * @param uploadFile The file to validate
 * @returns {string} Error message or empty string if valid
 */
export const validateFileWithErrorMessage = (uploadFile: File): string => {
  if (!uploadFile) return verbiage.unrecognizableFile;
  if (uploadFile.size > 20000000) return verbiage.fileTooLarge;
  if (!isSupportedFileType(uploadFile)) return verbiage.fileNotSupported;
  if (!fileNameIsValid(uploadFile.name)) return verbiage.fileNameFormatInvalid;
  return '';
};

export const validateFile = (uploadFile: File): boolean => {
  if (!uploadFile || uploadFile.size > 20000000) {
    return false;
  }
  return isSupportedFileType(uploadFile) && fileNameIsValid(uploadFile.name);
}

export const fetchDownloadFile = (
  reportUrl: string,
  onSuccess?: () => void,
  onError?: (err: Error) => void
): void => {
  request
    .get(reportUrl)
    .responseType('blob')
    .then(res => {
      const {body, headers} = res;
      const rawFileName = headers['content-disposition'].split('filename=')[1];
      const match = rawFileName?.match(/"([^"]+)"/);
      const filename = match?.[1] || 'download';

      downloadFileFromBlob(body, filename);
      onSuccess && onSuccess();
    }, (err: Error) => {
      if (onError) {
        onError(err);
      } else {
        throw err;
      }
    });
};

const downloadFileFromBlob = (blob: Blob, fileName: string): void => {
  if (blob) {
    const url = window.URL.createObjectURL(
      new Blob([blob]),
    );

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);

    const clickEvt = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    link.dispatchEvent(clickEvt);
    link.remove();
  }
}
